import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";
import AxiosProvider2 from "../AxiosProvider2";

function TableServicesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [serviceItems, setServiceItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedServices, setSelectedServices] = useState({});
  const [note, setNote] = useState("");
  const isRtl = i18n.language === "ar" || i18n.language === "ur";
  const hasSelectedItems = Object.values(selectedServices).some(Boolean);

  useEffect(() => {
    const fetchSessionServices = async () => {
      try {
        setLoading(true);
        const response = await AxiosProvider2.get("/session-services", {
          headers: {
            language: i18n.language,
          },
        });
        setServiceItems(response?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch session services:", error);
        setServiceItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionServices();
  }, [i18n.language]);

  const toggleCardSelection = (id) => {
    setSelectedServices((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSubmit = async () => {
    const selectedServiceIds = Object.entries(selectedServices)
      .filter(([, isSelected]) => isSelected)
      .map(([id]) => ({ id: Number(id) }));

    const tableId = Number(localStorage.getItem("tableId"));

    if (!selectedServiceIds.length || !tableId) {
      return;
    }

    try {
      setSubmitting(true);
      await AxiosProvider2.post("/table-services", {
        services: selectedServiceIds,
        tableId,
        note,
      });
      navigate(-1);
    } catch (error) {
      console.error("Failed to submit table services:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="min-h-screen bg-white px-4 py-6"
    >
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-3xl flex-col">
        <div className="mb-5 flex items-center justify-start gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-stone-700"
          >
            {isRtl ? (
              <AiOutlineRight className="h-4 w-4" />
            ) : (
              <AiOutlineLeft className="h-4 w-4" />
            )}
          </button>
          <h1 className="text-xl font-semibold text-stone-800 font-cairo">
            {t("TableServices.title")}
          </h1>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {loading && (
            <div className="col-span-3 py-4 text-center text-sm text-stone-500">
              {t("TableServices.loading")}
            </div>
          )}
          {serviceItems.map((item) => {
            const isActive = !!selectedServices[item.id];

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => toggleCardSelection(item.id)}
                className={`h-28 rounded-xl bg-white px-2 py-3 ${
                  isActive ? "border-2 border-[#E57200]" : "border border-gray-300"
                }`}
              >
                <div className="flex h-full flex-col items-center justify-center gap-3">
                  <img src={item.image} alt={item.name} className="h-10 w-10" />
                  <span className="text-center text-xs font-semibold text-stone-700 font-cairo">
                    {item.name}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {hasSelectedItems && (
          <div className="mt-auto flex w-full flex-col gap-3">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("TableServices.notePlaceholder")}
              className="w-full rounded-xl border border-gray-300 p-3 text-sm text-stone-700 outline-none focus:border-[#E57200]"
              rows={3}
            />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full rounded-xl bg-[#E57200] py-3 text-base font-semibold text-white font-cairo"
            >
              {submitting ? t("TableServices.loading") : t("TableServices.done")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TableServicesPage;
